/**
 * Время перезвона для метки «Просил перезвонить».
 *
 * Пресеты покрывают типовые договорённости («через час», «завтра утром»),
 * точное время вводится полем. Всё время — МОСКОВСКОЕ (§6.7): браузер
 * диспетчера может стоять в любом поясе, а договорённость с абонентом
 * называется по его и нашим рабочим часам, не по часам браузера.
 *
 * Сервер дополнительно прижимает выбранный момент к рабочему окну схемы —
 * «перезвоните в 23:30» не заставит звонить ночью (clampToWorkWindow, §6.6).
 */

import { businessDay, CALLBACK_MAX_DAYS, MOSCOW_TZ, zonedTimeToUtc } from '@cc/shared';

export type CallbackPreset = { key: string; title: string; at: (now: Date) => Date };

export const CALLBACK_PRESETS: readonly CallbackPreset[] = [
  { key: 'h1', title: 'Через час', at: (now) => addHours(now, 1) },
  { key: 'h2', title: 'Через 2 часа', at: (now) => addHours(now, 2) },
  { key: 'tomorrow10', title: 'Завтра в 10:00', at: (now) => tomorrowMskAt(now, '10:00:00') },
  { key: 'tomorrow18', title: 'Завтра в 18:00', at: (now) => tomorrowMskAt(now, '18:00:00') },
];

function addHours(now: Date, hours: number): Date {
  return new Date(now.getTime() + hours * 3_600_000);
}

/** «Завтра» — от московских рабочих суток, а не от полуночи браузера. */
function tomorrowMskAt(now: Date, timeOfDay: string): Date {
  const today = businessDay(now, MOSCOW_TZ);
  return zonedTimeToUtc(addDays(today, 1), timeOfDay, MOSCOW_TZ);
}

function addDays(day: string, days: number): string {
  const y = Number(day.slice(0, 4));
  const m = Number(day.slice(5, 7));
  const d = Number(day.slice(8, 10));
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/**
 * Значение `<input type="datetime-local">` («2026-08-30T15:00») → момент,
 * прочитанный КАК МОСКОВСКОЕ время. Строка без зоны, и разбирать её встроенным
 * Date значило бы получить зону браузера. null — поле пустое или битое.
 */
export function parseMskDateTimeLocal(value: string): Date | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/.exec(value);
  if (!m) return null;
  const [, day, hhmm] = m;
  if (day === undefined || hhmm === undefined) return null;
  return zonedTimeToUtc(day, `${hhmm}:00`, MOSCOW_TZ);
}

/** Момент → значение для `<input type="datetime-local">`, в МСК. */
export function toMskDateTimeLocal(at: Date): string {
  const fmt = new Intl.DateTimeFormat('sv-SE', {
    timeZone: MOSCOW_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  // sv-SE даёт «2026-08-30 15:00» — остаётся заменить пробел на T
  return fmt.format(at).replace(' ', 'T');
}

/**
 * Время в прошлом бессмысленно: карточка всплыла бы немедленно, а диспетчер
 * был бы уверен, что перенёс звонок. Далёкое будущее — почти всегда опечатка
 * в годе: такой контакт молча выпал бы из прозвона до самой даты. Ту же пару
 * границ держит сервер (applyLabel) — здесь они ловятся до отправки.
 */
export function callbackTimeIssue(at: Date | null, now: Date): string | null {
  if (at === null) return 'Выберите, когда перезвонить';
  if (at.getTime() <= now.getTime()) return 'Это время уже прошло — выберите будущее';
  if (at.getTime() > now.getTime() + CALLBACK_MAX_DAYS * 86_400_000) {
    return `Перезвон не дальше ${String(CALLBACK_MAX_DAYS)} дней — проверьте год и месяц`;
  }
  return null;
}

/** Границы для `<input type="datetime-local">`: от «сейчас» до горизонта перезвона, МСК. */
export function callbackInputBounds(now: Date): { min: string; max: string } {
  return {
    min: toMskDateTimeLocal(now),
    max: toMskDateTimeLocal(new Date(now.getTime() + CALLBACK_MAX_DAYS * 86_400_000)),
  };
}
